import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { privacyApi } from '../../../api/privacy.js';
import { ApiError } from '../../../api/client.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { KpiCard } from '../../../components/ui/KpiCard.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

import { RegisterFormModal } from './RegisterFormModal.jsx';

const STATUS_TONE = { active: 'success', draft: 'warning', retired: 'neutral' };
const STATUS_LABEL = { active: 'Attiva', draft: 'Bozza', retired: 'Dismessa' };

export function RegisterPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { run, mfaProps } = useMfaStepUp();

  const canManage = hasPermission('privacy:manage');
  const canExport = hasPermission('privacy:export');

  const overviewQuery = useQuery({
    queryKey: ['privacy', 'overview'],
    queryFn: privacyApi.overview
  });
  const overview = overviewQuery.data;

  const { items, isLoading, pagination, page, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['privacy', 'register'],
    fetcher: privacyApi.listRegister
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => run(() => privacyApi.removeRegister(id)),
    onSuccess: () => {
      toast.success('Voce eliminata.');
      queryClient.invalidateQueries({ queryKey: ['privacy', 'register'] });
      queryClient.invalidateQueries({ queryKey: ['privacy', 'overview'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const res = await privacyApi.exportRegister();
      if (!res.ok) throw new ApiError({ code: 'HTTP_ERROR', message: `HTTP ${res.status}`, statusCode: res.status });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `privacy-register-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('Registro esportato.'),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const columns = [
    { key: 'name', header: 'Attività' },
    {
      key: 'legalBases',
      header: 'Basi giuridiche',
      render: (r) => (r.legalBases && r.legalBases.length) ? (
        <div className="flex flex-wrap gap-1">
          {r.legalBases.slice(0, 2).map((b) => <Badge key={b}>{b}</Badge>)}
          {r.legalBases.length > 2 && <Badge>+{r.legalBases.length - 2}</Badge>}
        </div>
      ) : <span className="text-ink-faint">-</span>
    },
    {
      key: 'retentionDays',
      header: 'Conservazione',
      align: 'right',
      render: (r) => r.retentionDays ? `${fmt.int(r.retentionDays)} gg` : <span className="text-ink-faint">-</span>
    },
    {
      key: 'dpiaRequired',
      header: 'DPIA',
      render: (r) => r.dpiaRequired ? <Badge tone="warning">richiesta</Badge> : <Badge>no</Badge>
    },
    {
      key: 'status',
      header: 'Stato',
      render: (r) => <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{STATUS_LABEL[r.status] || r.status}</Badge>
    },
    {
      key: 'updatedAt',
      header: 'Aggiornata',
      render: (r) => <span className="text-xs text-ink-muted">{fmt.date(r.updatedAt || r.createdAt)}</span>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          {canManage && (
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => { setEditing(r); setFormOpen(true); }}>
              Modifica
            </button>
          )}
          {canManage && (
            <button type="button" className="text-xs text-danger hover:underline" onClick={() => setConfirmTarget(r)}>
              Elimina
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Registro art. 30 GDPR"
        description="Attività di trattamento dei dati personali documentate per la conformità."
        actions={
          <div className="flex gap-2">
            {canExport && (
              <Button variant="secondary" loading={exportMutation.isPending} onClick={() => exportMutation.mutate()}>
                Esporta JSON
              </Button>
            )}
            {canManage && (
              <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuova attività</Button>
            )}
          </div>
        }
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Consensi attivi"
          loading={overviewQuery.isLoading}
          value={overview ? fmt.int(overview.consents?.active || 0) : '-'}
          sub={overview ? `${fmt.int(overview.consents?.revoked || 0)} revocati` : null}
        />
        <KpiCard
          label="Esportazioni dati"
          loading={overviewQuery.isLoading}
          value={overview ? fmt.int(overview.dataExport?.downloaded || 0) : '-'}
          sub={overview ? `${fmt.int(overview.dataExport?.ready || 0)} pronte da scaricare` : null}
        />
        <KpiCard
          label="Richieste di cancellazione"
          loading={overviewQuery.isLoading}
          value={overview ? fmt.int(overview.erasure?.completed || 0) : '-'}
          sub={overview ? `${fmt.int(overview.erasure?.pending || 0)} in attesa` : null}
        />
      </section>

      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="w-64"
          label="Cerca"
          placeholder="nome attività..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
        <Select
          label="Stato"
          className="w-40"
          value={filters.status || ''}
          onChange={(e) => setFilters({ status: e.target.value || undefined })}
        >
          <option value="">Tutti</option>
          <option value="active">Attive</option>
          <option value="draft">Bozze</option>
          <option value="retired">Dismesse</option>
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{
          title: 'Nessuna attività registrata',
          description: canManage ? 'Documenta la prima attività di trattamento per essere conforme all\'art. 30.' : ''
        }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <RegisterFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        initial={editing}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare la voce?"
        description={confirmTarget ? `L'attività "${confirmTarget.name}" sarà rimossa. Servirà una verifica MFA recente.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />

      <MfaPrompt {...mfaProps} />
    </div>
  );
}
